import { useCallback, useState } from "react";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View } from "@src/components/View";
import { Text } from "@src/components/Text";
import { ScreenContainer } from "@src/components/ScreenContainer";
import { Button } from "@src/components/Button";
import { FormInput } from "@src/components/FormInput";
import { SystemBars } from "react-native-edge-to-edge";
import { BackgroundGradient } from "../../../components/BackgroundGradient";
import { GradientBorderWrapper } from "@src/components/GradientBorderWrapper";
import { ContactUsButton } from "@src/components/ContactUsButton";
import { EnvironmentResetBadge } from "@src/components/EnvironmentResetBadge";
import LogoIcon from "@src/assets/svgs/logo-icon.svg";
import LogoType from "@src/assets/svgs/logo-type.svg";
import { emailSchema } from "../../../schemas";

interface SignInScreenProps {
  /**
   * Called with the validated email once the user submits the form. The
   * caller is responsible for resolving the Auth0 connection from the email
   * domain and starting the authorization request.
   */
  onSignIn: (email: string) => void;
  /** Disable the form (e.g. while the Auth0 request object is still building). */
  disabled: boolean;
}

export function SignInScreen({ onSignIn, disabled }: SignInScreenProps) {
  const { theme } = useUnistyles();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  const handleChangeEmail = useCallback(
    (value: string) => {
      setEmail(value);
      // Clear any stale validation error as soon as the user keeps typing.
      if (error) setError(undefined);
    },
    [error],
  );

  const handleSubmit = useCallback(() => {
    const trimmed = email.trim();
    const result = emailSchema.safeParse(trimmed);
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? "Please enter a valid email address");
      return;
    }
    setError(undefined);
    onSignIn(result.data);
  }, [email, onSignIn]);

  const isSubmitDisabled = disabled || email.trim().length === 0;

  return (
    <>
      <SystemBars style="light" />
      <BackgroundGradient>
        <ScreenContainer edges={["top", "bottom"]} style={{ backgroundColor: "transparent" }}>
          <View justifyContent="center" alignItems="center" mt="8xl">
            <View flexDirection="row" alignItems="center" gap="md">
              <LogoIcon width={75} height={70} />
              <LogoType width={195} height={70} color={theme.colors.interactive.buttons.default_foreground} />
            </View>
          </View>
          <EnvironmentResetBadge />
          <View flex={1} alignItems="center" px="6xl" mt="14xl">
            <View mb="6xl">
              <Text textStyle="h3/regular" textAlign="center" style={styles.text}>
                Welcome to Visioni.
              </Text>
            </View>
            <View mb="6xl">
              <Text textStyle="sm/regular" textAlign="center" style={styles.text}>
                Sign in to access your alarm schedules{"\n"}and camera monitoring.
              </Text>
            </View>
            <View w="100%" maxW={320} mb="2xl">
              <FormInput
                value={email}
                onChangeText={handleChangeEmail}
                placeholder="Email Address"
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                returnKeyType="go"
                onSubmitEditing={handleSubmit}
                error={error}
                variant="outline"
                editable={!disabled}
              />
            </View>
            <View w="100%" maxW={255}>
              <GradientBorderWrapper>
                <Button
                  label="Sign In"
                  variant="primary"
                  onPress={handleSubmit}
                  disabled={isSubmitDisabled}
                  size="md"
                  textStyle="md/regular"
                />
              </GradientBorderWrapper>
            </View>
            <Text textStyle="sm/regular" textAlign="center" mt="xl" style={styles.text}>
              Secured by Auth0
            </Text>
          </View>
          <View flexDirection="row" justifyContent="center" alignItems="center" mb="xl" gap="xs">
            <Text textStyle="sm/regular" textAlign="center" style={styles.text}>
              Having a problem?
            </Text>
            <ContactUsButton />
          </View>
        </ScreenContainer>
      </BackgroundGradient>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
}));
